import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { Subscription, interval, of } from 'rxjs';
import { catchError, startWith, switchMap } from 'rxjs/operators';
import { MenuService } from 'app';
import { I18nService } from 'i18n';
import { CommunityMessagesService } from './community-messages.service';

/**
 * Unread private messages: one number, shown next to "Messages" inside the
 * community shell and as a badge on the sidebar's Community entry.
 *
 * Polls `/messages/unread_count` every minute while a consumer holds the
 * service open (`start()` / `stop()` are reference-counted, so the shell and
 * the messages pages may overlap without doubling the requests). A single
 * `refresh()` is enough for anything that merely wants the badge up to date
 * (the dashboard widget, after reading a message).
 */
@Injectable({ providedIn: 'root' })
export class CommunityUnreadService implements OnDestroy {
  private messages = inject(CommunityMessagesService);
  private menuService = inject(MenuService);
  private i18n = inject(I18nService);

  private static readonly POLL_INTERVAL_MS = 60_000;

  readonly count = signal(0);

  private holders = 0;
  private polling: Subscription | null = null;
  private langSub: Subscription | null = null;
  /** The menu url the badge was last applied to; cleared before re-applying under a new language. */
  private badgedUrl: string | null = null;

  start(): void {
    this.holders += 1;
    if (this.polling) return;
    this.polling = interval(CommunityUnreadService.POLL_INTERVAL_MS)
      .pipe(
        startWith(0),
        switchMap(() => this.messages.unreadCount().pipe(catchError(() => of(null))))
      )
      .subscribe(res => {
        if (res) this.apply(res.unread_count);
      });
    // The Community menu url carries the language prefix, so the badge has to
    // follow a language switch.
    this.langSub ??= this.i18n.currentLanguage$.subscribe(() => this.apply(this.count()));
  }

  stop(): void {
    this.holders = Math.max(0, this.holders - 1);
    if (this.holders > 0) return;
    this.polling?.unsubscribe();
    this.polling = null;
  }

  refresh(): void {
    this.messages.unreadCount().pipe(catchError(() => of(null))).subscribe(res => {
      if (res) this.apply(res.unread_count);
    });
  }

  private apply(count: number): void {
    this.count.set(count);
    const url = `/${this.i18n.getLanguage()}/community`;
    if (this.badgedUrl && this.badgedUrl !== url) this.menuService.updateMenuBadge(this.badgedUrl, undefined);
    this.badgedUrl = url;
    // Matching is `url.includes(pattern)`, so the pattern must carry the
    // language segment: bare `/community` would also badge `/admin/community`.
    this.menuService.updateMenuBadge(url, count > 0 ? { color: 'danger', text: count > 99 ? '99+' : String(count) } : undefined);
  }

  ngOnDestroy(): void {
    this.polling?.unsubscribe();
    this.langSub?.unsubscribe();
  }
}
